<template>
  <modal-component
    :is-displayed="isNewMacModalOpened"
    :no-padding="true"
    :title="$t('ICDManagement.CreateTitleMac')"
    @toggle-modal="$emit('toggle-modal')"
  >
    <form-wrapper :is-form-in-modal="true">
      <form @submit.prevent="handleSubmit">
        <form-divider>
          <div class="flex items-center gap-2">
            <text-box-mask
              :label="$t('ICDManagement.MAC')"
              :vModel="sensor.mac"
              :value="sensor.mac"
              :maskRules="rules"
              :inputAttr="{
                'aria-label': 'Mask',
                style: 'text-transform: uppercase',
              }"
              :showClearButton="false"
              :errorMessage="v$?.sensor['mac']?.$errors[0]?.$message"
              :isError="v$?.sensor['mac']?.$error"
              mask="XX:XX:XX:XX:XX:XX"
              maskInvalidMessage="Inserted address does not match a mac address."
              valueChangeEvent="keyup"
              @value-changed="onValueChange"
            />
          </div>
        </form-divider>
        <button-wrapper :is-submit-loading="isSubmitLoading" />
      </form>
    </form-wrapper>
  </modal-component>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import ModalComponent from "@/core/Components/Modal.vue";
import FormWrapper from "@/core/Components/FormElements/FormWrapper.vue";
import FormDivider from "@/core/Components/FormElements/FormDivider.vue";
import SmallLoader from "@/core/Components/SmallLoader.vue";
import VSelect from "vue-select";
import ButtonWrapper from "@/core/Components/FormElements/ButtonWrapper.vue";
import TheButton from "@/core/Components/TheButton.vue";
import { useToast } from "vue-toastification";
import {
  ErrorMessageFormatter,
  formatMacAddress,
  formatMacAddressBackToString,
  isString,
} from "@/core/Services/GlobalFunctions";
import useValidate from "@vuelidate/core";
import { maxLength, minLength, required } from "@vuelidate/validators";
import TextBoxMask from "@/core/Components/FormElements/TextBoxMask.vue";

import "devextreme/dist/css/dx.light.css";

import { DxTextBox } from "devextreme-vue/text-box";
import { CreateMac } from "../../Services/ICDManagementTypes";
import IcdService from "../../Services/ICDManagementService";

export default defineComponent({
  components: {
    ButtonWrapper,
    VSelect,
    SmallLoader,
    FormDivider,
    FormWrapper,
    ModalComponent,
    TextBoxMask,
    DxTextBox,
    TheButton,
  },
  props: {
    isNewMacModalOpened: Boolean,
    mac: String,
  },
  emits: ["toggle-modal", "get-mac", "new-mac-created"],
  data() {
    return {
      toast: useToast(),
      v$: useValidate(),
      icdService: new IcdService(),
      validationErrors: [] as any,
      isSubmitLoading: false,
      sensor: {
        mac: "",
      } as CreateMac,
      rules: {
        X: /[0-9A-Fa-f]/,
      },
    };
  },
  validations() {
    return {
      sensor: {
        mac: {
          required,
        },
      },
    };
  },

  async created() {
    try {
      if (this.mac) {
        this.sensor.mac = formatMacAddressBackToString(this.mac);
      }
    } catch (error: any) {
      if (!isString(error.response.data)) {
        this.toast.error(ErrorMessageFormatter(error));
        return;
      }
      this.toast.error(error.response.data);
    }
  },
  methods: {
    async handleSubmit() {
      this.v$.$validate();
      this.validationErrors = this.v$.$errors;
      if (!this.v$.$error) {
        this.isSubmitLoading = true;
        try {
          this.sensor.mac = formatMacAddress(this.sensor.mac).toUpperCase();
          await this.icdService.createMac(this.sensor);
          this.$emit("get-mac", this.sensor.mac);
          this.$emit("new-mac-created");
          this.$emit("toggle-modal");
          this.toast.success("ICD Assigned successfully!");
        } catch (error: any) {
          if (!isString(error.response.data)) {
            this.toast.error(ErrorMessageFormatter(error));
            return;
          }
          this.toast.error(error.response.data);
        } finally {
          this.isSubmitLoading = false;
        }
      }
    },

    onValueChange(e: any) {
      try {
        const previousValue = e.previousValue;
        const newValue = e.value;

        previousValue !== newValue ? (this.sensor.mac = newValue) : (this.sensor.mac = previousValue);
      } catch (error: any) {
        if (!isString(error.response.data)) {
          this.toast.error(ErrorMessageFormatter(error));
          return;
        }
        this.toast.error(error.response.data);
        throw error;
      }
    },
  },
});
</script>

<style scoped></style>
